import React, { useEffect, useState } from "react";
import cx from "classnames";
import { Heading, Text, Button, RadioButton } from "@foris/avocado-suite";
import { ClassroomBooking, ClassroomBookingStatusEnum } from "@models/ISchema";
import * as utils from "../../utils";
import Textarea from "@common/components/Textarea";
import css from "./detail.module.scss";
import { useTranslation } from "react-i18next";
import useBookingAccess from "@common/hooks/useBookingAccess";

interface Props {
  editing: boolean;
  booking: ClassroomBooking;
  onClick: () => void;
  onConfirmation: (state: ClassroomBookingStatusEnum) => void;
  onNotification: (value: string) => void;
  onCancel: () => void;
  onSave: (status: string, comment: string) => void;
  statusConfirmation: string;
  notificationText: string;
}

const State: React.FC<Props> = ({
  booking,
  editing,
  onClick,
  onConfirmation,
  onNotification,
  onCancel,
  onSave,
  statusConfirmation,
  notificationText,
}) => {
  const { t } = useTranslation();
  const { isAdminBooking } = useBookingAccess();
  const [confirmationText, setConfirmationText] = useState("");
  const [bookingStatus, setBookingStatus] = useState(booking?.status);

  useEffect(() => {
    if (!editing) {
      setConfirmationText("");
    } else {
      setBookingStatus(booking?.status);
    }
  }, [editing]);

  return (
    <div className={css.detail}>
      <header className={css.detail_header}>
        <div className={css.headerTitle}>
          <Heading className={css.headerTitle_text} type="h3">
            {t("booking.booking-details.status-resume.title")}
          </Heading>
        </div>
      </header>

      {!editing ? (
        <>
          <section className={css.detail_resume}>
            <div className={css.resumeItem}>
              <Text className={css.resumeItem_label}>
                {t("booking.booking-details.status-resume.status")}
              </Text>
              <Text className={css.resumeItem_value}>{utils.icons.iconText(booking?.status)}</Text>
            </div>
          </section>

          {isAdminBooking && (
            <Button variant="secondary" className={css.detail_editBtn} onClick={onClick}>
              {t("booking.booking-details.actions.edit-booking")}
            </Button>
          )}
        </>
      ) : (
        <>
          <div className={css.detail_statusForm}>
            <div className={css.formRadioButtons}>
              <RadioButton
                labelRight={t("booking.booking-details.status-resume.confirm")}
                name="manage"
                className={css.cardDetail_radioBtn}
                checked={bookingStatus === ClassroomBookingStatusEnum.Confirmed}
                onChange={() => {
                  setBookingStatus(ClassroomBookingStatusEnum.Confirmed);
                  onConfirmation(ClassroomBookingStatusEnum.Confirmed);
                }}
              />
              <RadioButton
                labelRight={t("booking.booking-details.status-resume.reject")}
                name="manage"
                className={css.cardDetail_radioBtn}
                checked={bookingStatus === ClassroomBookingStatusEnum.Rejected}
                onChange={() => {
                  setBookingStatus(ClassroomBookingStatusEnum.Rejected);
                  onConfirmation(ClassroomBookingStatusEnum.Rejected);
                }}
              />
            </div>

            <Textarea
              label={t("booking.booking-details.status-resume.notification")}
              className={cx(css.cardDetail_textarea, "col_12")}
              value={confirmationText}
              onChange={value => {
                setConfirmationText?.(value);
                onNotification?.(value);
              }}
            />
          </div>

          <div className={css.detail_actions}>
            <Button variant="ghost" className={css.cardDetail_btn} onClick={() => onCancel()}>
              {t("booking.booking-details.actions.cancel")}
            </Button>

            <Button
              className={css.cardDetail_btn}
              disabled={!statusConfirmation || bookingStatus === booking?.status}
              onClick={() => onSave(statusConfirmation, notificationText)}
            >
              {t("booking.booking-details.actions.save")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default State;
